import * as React from 'react'

import * as styles from './getFeedback.module.css'

import { Site } from '@envato/foundation-types'
import Button, { ButtonAppearance, ButtonSize } from '@envato/foundation-button'

import { getConfigBySite } from '../../../config/sites'
import { Consent } from '../../../common/utils/consent'
import { getCookie, setCookie } from '../../../common/utils/cookieHelper'
import { currentViewsForSurvey } from '../../../common/utils/tracking/trackPageViewForSurvey'
import ga from '../../../common/utils/ga'

import IconClose from '../icons/close'

interface IProps {
  currentSite: Site
  openModal: (arg1: unknown) => void
  closeModal: () => void
  isModalOpen: boolean
}

const PAGE_VIEW_COUNT = 5
const AUTO_CLOSE_MS = 3500

function sendAnalyticsEvent(eventLabel?: string) {
  ga('send', {
    hitType: 'event',
    eventCategory: 'csatmodal;pop-up',
    eventAction: 'view',
    ...(eventLabel ? { eventLabel } : {})
  })
}

const GetFeedback = ({
  currentSite,
  openModal,
  closeModal,
  isModalOpen
}: IProps) => {
  const siteName = getConfigBySite(currentSite, 'name')
  const surveyURL = getConfigBySite(currentSite, 'CSATFeedbackURL')

  if (!surveyURL) return null

  const [hasSeenAlert, setHasSeenAlert] = React.useState(true)
  const pageViewCount = currentViewsForSurvey()

  React.useEffect(() => {
    setHasSeenAlert(
      getCookie({
        itemKey: 'feedback_survey_complete',
        consentType: Consent.preferences,
        defaultValue: 'false'
      }) === 'true'
    )
  }, [setHasSeenAlert])

  React.useEffect(() => {
    if (!isModalOpen) return

    const handleSurveySubmitted = (event: any) => {
      if (
        event.origin === 'https://www.getfeedback.com' &&
        typeof event.data === 'string' &&
        // GetFeedback provides the data in the format of `eventName:surveyId`
        event.data.split(':')[0] === 'submittedResponse'
      ) {
        setTimeout(() => {
          closeModal()
        }, AUTO_CLOSE_MS)
      }
    }

    window.addEventListener('message', handleSurveySubmitted)

    return () => {
      window.removeEventListener('message', handleSurveySubmitted)
    }
  }, [isModalOpen, closeModal])

  if (hasSeenAlert || pageViewCount < PAGE_VIEW_COUNT) return null

  function closeFeedbackAlert(eventLabel: string) {
    sendAnalyticsEvent(eventLabel)
    setHasSeenAlert(true)
    setCookie({
      itemKey: 'feedback_survey_complete',
      consentType: Consent.preferences,
      itemValue: 'true'
    })
  }

  function launchFeedbackModal() {
    openModal({
      type: 'getFeedback',
      payload: { surveyURL }
    })

    closeFeedbackAlert('sure take me to survey')
  }

  sendAnalyticsEvent()

  return (
    <div className={styles.root}>
      <button
        className={styles.closeButton}
        onClick={() => closeFeedbackAlert('x')}
      >
        <IconClose />
      </button>
      <div className={styles.alertDetail}>
        <p className={styles.title}>Tell us what you think!</p>
        <p className={styles.subtitle}>
          We&apos;d like to ask you a few questions to help improve {siteName}.
        </p>

        <Button
          className={styles.ctaButton}
          appearance={ButtonAppearance.Primary}
          onClick={launchFeedbackModal}
          size={ButtonSize.Small}
        >
          Sure, take me to the survey
        </Button>
      </div>
    </div>
  )
}

export default GetFeedback
